import React from "react";
import { Card, CardContent } from "../components/ui/card";
import {
  CheckCircle2,
  Users,
  Shield,
  Package,
  Truck,
  ArrowRight,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const ProcessPage = () => {
  const processSteps = [
    {
      icon: <Users className="h-10 w-10 text-emerald-700" />,
      title: "Farmer Connection",
      description:
        "We connect local farmers directly with domestic and international buyers to create better market opportunities.",
    },
    {
      icon: <Shield className="h-10 w-10 text-amber-700" />,
      title: "Quality Inspection",
      description:
        "Our team visits farms personally to inspect product quality and ensure export-grade standards.",
    },
    {
      icon: <Package className="h-10 w-10 text-emerald-700" />,
      title: "Custom Packaging",
      description:
        "Products are packed according to buyer requirements using safe and export-quality packaging.",
    },
    {
      icon: <Truck className="h-10 w-10 text-amber-700" />,
      title: "Delivery & Supply",
      description:
        "We manage logistics, shipping, and delivery to ensure products reach buyers safely and on time.",
    },
  ];

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white">
        {/* Hero Section */}
        <section className="py-24 text-center bg-emerald-900 text-white">
          <div className="max-w-4xl mx-auto px-4">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Our Working Process
            </h1>

            <p className="text-xl text-emerald-100 leading-relaxed">
              From farm selection to global delivery, we ensure quality,
              transparency, and reliable supply chain management at every step.
            </p>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-16 lg:gap-8 items-center">
              {processSteps.map((step, index) => (
                <div key={index} className="relative flex items-center">
                  {/* Card */}
                  <Card className="w-full hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-t-emerald-600 relative overflow-hidden">
                    {/* Step Number */}
                    <div className="absolute top-4 right-4 w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-lg">
                      {index + 1}
                    </div>

                    <CardContent className="p-8">
                      <div className="mb-6">{step.icon}</div>

                      <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {step.title}
                      </h3>

                      <p className="text-gray-600 leading-relaxed">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>

                  {/* Responsive Arrow */}
                  {index !== processSteps.length - 1 && (
                    <div className="absolute left-1/2 lg:left-auto lg:-right-6 bottom-[-38px] lg:bottom-auto lg:top-1/2 transform -translate-x-1/2 lg:translate-x-0 lg:-translate-y-1/2 z-10 flex">
                      <div className="bg-white shadow-lg rounded-full p-3 border border-emerald-100">
                        <ArrowRight className="h-6 w-6 text-emerald-600 rotate-90 lg:rotate-0" />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Bottom Features */}
            <div className="mt-20 grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition">
                <CheckCircle2 className="h-8 w-8 text-emerald-600 mb-4" />

                <h4 className="text-xl font-semibold text-gray-900 mb-3">
                  Farm to Market
                </h4>

                <p className="text-gray-600">
                  Direct sourcing from farms to local and international markets
                  with complete transparency.
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition">
                <CheckCircle2 className="h-8 w-8 text-emerald-600 mb-4" />

                <h4 className="text-xl font-semibold text-gray-900 mb-3">
                  Export Quality
                </h4>

                <p className="text-gray-600">
                  Strict inspection and packaging procedures ensure premium
                  export standards.
                </p>
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition">
                <CheckCircle2 className="h-8 w-8 text-emerald-600 mb-4" />

                <h4 className="text-xl font-semibold text-gray-900 mb-3">
                  Reliable Delivery
                </h4>

                <p className="text-gray-600">
                  Efficient logistics and supply chain management guarantee
                  timely deliveries worldwide.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
};

export default ProcessPage;
